import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { mediaSrc } from "@/lib/api";

/**
 * Onedot6 immersive image viewer — the approved gallery UX:
 * ONE main image + dynamic counter + swipe / simple navigation + ONE close.
 * No thumbnail grid. Dimmed, scroll-locked, keyboard + swipe navigable.
 */
export default function Lightbox({ images, startIndex = 0, onClose, alt = "Gallery image", testId }) {
  const clean = (images || []).filter((u) => typeof u === "string" && u.trim());
  const total = clean.length;
  const [idx, setIdx] = useState(Math.min(startIndex, Math.max(total - 1, 0)));
  const touchStartX = useRef(null);
  const closeRef = useRef(null);

  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);

  // Keyboard nav + body-scroll lock
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, prev, next]);

  // Focus management: focus close on open, return focus to opener on close
  useEffect(() => {
    const opener = document.activeElement;
    closeRef.current?.focus();
    return () => {
      try { opener?.focus?.(); } catch { /* opener may be gone */ }
    };
  }, []);

  // Preload neighbouring images for snappy navigation
  useEffect(() => {
    if (total < 2) return;
    [idx - 1, idx + 1].forEach((n) => {
      const src = mediaSrc(clean[(n + total) % total]);
      if (src) { const im = new Image(); im.src = src; }
    });
  }, [idx, total, clean]);

  if (total === 0) return null;

  const onPointerDown = (e) => { touchStartX.current = e.clientX; };
  const onPointerUp = (e) => {
    if (touchStartX.current == null || total < 2) { touchStartX.current = null; return; }
    const dx = e.clientX - touchStartX.current;
    if (Math.abs(dx) > 45) { dx < 0 ? next() : prev(); }
    touchStartX.current = null;
  };

  const arrowBtn = "absolute top-1/2 -translate-y-1/2 w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors backdrop-blur-sm";

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery"
      className="fixed inset-0 z-[300] backdrop-blur-md flex items-center justify-center select-none"
      style={{ backgroundColor: "rgba(10,9,8,0.97)", touchAction: "pan-y" }}
      data-testid={testId}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      {/* ONE close button */}
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Close gallery"
        data-testid="lightbox-close"
        className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-11 h-11 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white/85 hover:text-white transition-colors backdrop-blur-sm"
      >
        <X size={22} strokeWidth={1.5} />
      </button>

      {/* Main image — dominates the viewport */}
      <img
        src={mediaSrc(clean[idx])}
        alt={`${alt} — image ${idx + 1} of ${total}`}
        className="max-h-[82vh] max-w-[92vw] w-auto h-auto object-contain shadow-[0_10px_60px_rgba(0,0,0,0.5)]"
        data-testid={`lightbox-img`}
        draggable={false}
      />

      {/* Subtle navigation (swipe is primary on touch) */}
      {total > 1 && (
        <>
          <button type="button" onClick={prev} aria-label="Previous image" data-testid="lightbox-prev" className={`${arrowBtn} left-3 md:left-6`}>
            <ChevronLeft size={22} strokeWidth={1.5} />
          </button>
          <button type="button" onClick={next} aria-label="Next image" data-testid="lightbox-next" className={`${arrowBtn} right-3 md:right-6`}>
            <ChevronRight size={22} strokeWidth={1.5} />
          </button>
        </>
      )}

      {/* Discreet dynamic counter */}
      <span
        data-testid="lightbox-counter"
        aria-live="polite"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-[0.3em] tabular-nums"
      >
        {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
      </span>
    </div>,
    document.body
  );
}
